/**
 * RealtimeConnectionToast — tells the user when LIVE UPDATES pause and resume.
 *
 * WHY: Rüdiger saw repeated `websocket-factory.js:103` failures with no
 * visible signal in the app — he wondered why fresh leads weren't appearing.
 *
 * WHAT IT IS NOT (2026-10-27): it is not an offline signal. The Supabase
 * Realtime socket only drives cache refresh. Writes go through the Service
 * Layer, and "offline" is decided by the reachability probe
 * (`@appic/lib` offline/reachability.ts) and shown by OfflineIndicator. The old
 * copy ("Du arbeitest offline weiter – deine Eingaben werden gespeichert")
 * claimed an offline state the socket cannot know, on a working network.
 *
 * The LOST toast is flap-debounced (see connectionFlapDebounce): a loss must
 * persist for FLAP_GRACE_MS (30 s) and the tab must be visible; a loss that
 * elapses while hidden surfaces on the next visibilitychange if still lost.
 * The RESTORED toast surfaces immediately, but only after a LOST was shown.
 * The re-fetch of missed changes on reconnect is done by useRealtimeSync
 * (`invalidateRealtimeQueries`), toast or not.
 *
 * Mount inside AppLayoutInner (which is inside both ToastProvider and
 * RealtimeStatusContext.Provider). No visible DOM.
 */

import { useEffect, useRef } from 'react'
import { useToast } from '@appic/ui-components'
import { useRealtimeStatus, type RealtimeConnectionStatus } from '@appic/hooks'
import { useLanguage } from '@/contexts/LanguageContext'
import {
  FLAP_GRACE_MS,
  INITIAL_FLAP_STATE,
  reduceFlap,
  tickFlap,
  type FlapAction,
  type FlapState,
} from './connectionFlapDebounce'

function isTabVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible'
}

export function RealtimeConnectionToast(): null {
  const { status } = useRealtimeStatus()
  const { toast } = useToast()
  const { t } = useLanguage()

  const prevStatusRef = useRef<RealtimeConnectionStatus | null>(null)
  const stateRef = useRef<FlapState>(INITIAL_FLAP_STATE)
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Stable toast trigger — reads the latest `toast`/`t` via refs so the
  // status effect doesn't re-subscribe (and re-arm timers) on every render.
  const toastRef = useRef(toast)
  const tRef = useRef(t)
  toastRef.current = toast
  tRef.current = t

  const fireRef = useRef((action: FlapAction): void => {
    if (action.kind === 'lost') {
      toastRef.current({
        variant: 'warn',
        title: tRef.current('realtime.connectionLost.title'),
        description: tRef.current('realtime.connectionLost.description'),
        durationMs: 8000,
      })
    } else if (action.kind === 'restored') {
      toastRef.current({
        variant: 'success',
        title: tRef.current('realtime.connectionRestored.title'),
        durationMs: 3500,
      })
    }
  })

  useEffect(() => {
    const fire = fireRef.current
    const clearGraceTimer = (): void => {
      if (graceTimerRef.current !== null) {
        clearTimeout(graceTimerRef.current)
        graceTimerRef.current = null
      }
    }

    const prev = prevStatusRef.current
    prevStatusRef.current = status

    const { action, state } = reduceFlap(prev, status, stateRef.current, Date.now())
    stateRef.current = state

    // Recovery (or any non-pending transition) cancels a still-pending loss.
    if (status === 'connected') {
      clearGraceTimer()
    }
    fire(action)

    // If a loss just armed its grace window, schedule the sustained-loss surface.
    if (state.pendingLostSince !== null && graceTimerRef.current === null) {
      graceTimerRef.current = setTimeout(() => {
        graceTimerRef.current = null
        const tick = tickFlap(stateRef.current, Date.now(), isTabVisible())
        stateRef.current = tick.state
        fire(tick.action)
      }, FLAP_GRACE_MS)
    }

    return clearGraceTimer
  }, [status])

  // A sustained loss that elapsed while the tab was hidden is still pending;
  // surface it when the user comes back — if it is still lost by then.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const onVisibility = (): void => {
      const tick = tickFlap(stateRef.current, Date.now(), isTabVisible())
      stateRef.current = tick.state
      fireRef.current(tick.action)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  return null
}
